import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

// ===================== models =====================

/** Write model for Insert/Update. `id` 0 = create, >0 = update. */
export interface ReportScheduleDto {
  id: number;
  scheduleName: string;
  reportType: string; // 'COI_GENERATED' | 'PAYMENTS'
  scopeType: string; // 'ALL' | 'PARTNER' | 'CLIENT'
  partnerId?: number | null; // required when scopeType = PARTNER
  clientId?: number | null; // required when scopeType = CLIENT
  paymentStatuses?: string | null; // PAYMENTS only — CSV; empty = all
  templateId?: number | null; // column template; null = all columns (default)
  reportWindow: string; // 'SAME_DAY' | 'PREVIOUS_DAY' | 'LAST_7_DAYS' | 'MONTH_TO_DATE' | 'ALL_TIME'
  frequency: string; // 'DAILY' | 'WEEKLY' | 'MONTHLY'
  runAtTime: string; // 'HH:mm' 24h, IST
  dayOfWeek?: number | null; // WEEKLY: 1=Mon .. 7=Sun
  dayOfMonth?: number | null; // MONTHLY: 1..28
  isEmail: boolean;
  isWhatsApp: boolean;
  emails?: string | null; // CSV
  whatsAppNumbers?: string | null; // CSV
  isActive: boolean;
}

/** Read model from GetAll/GetById — dto fields + joined names and UTC timestamps. */
export interface ReportScheduleRow extends ReportScheduleDto {
  partnerName?: string | null;
  clientName?: string | null;
  templateName?: string | null; // joined from the column template, when one is set
  fileFormat?: string | null; // always 'XLSX' today
  lastRunAt?: string | null; // UTC, may lack the trailing 'Z'
  nextRunAt?: string | null; // UTC, may lack the trailing 'Z'
  createdAt?: string | null;
}

export interface ReportRunLog {
  id: number;
  scheduleId: number;
  scheduleName?: string | null;
  triggeredBy: string; // 'CRON' | 'MANUAL'
  runStartedAt?: string | null; // UTC, may lack 'Z'
  runCompletedAt?: string | null;
  status: string; // 'SUCCESS' | 'NO_DATA' | 'RUNNING' | 'FAILED'
  rowCount: number;
  fileUrl?: string | null; // presigned S3 link (informational; expires) — download by key instead
  fileStorageKey?: string | null; // durable S3 object key; drives the auth'd download
  emailsSent: number;
  whatsAppSent: number;
  errorMessage?: string | null;
}

export interface IdName {
  id: number;
  name: string;
  code?: string | null;
}

export interface ValueLabel {
  value: number;
  label: string;
}

/** One selectable Excel column of a report type's catalogue (Options.reportColumns). */
export interface ReportColumnDef {
  key: string;
  label: string;
}

/** Active-template summary embedded in GET /Options (for the schedule dialog's select). */
export interface ReportTemplateOption {
  id: number;
  templateName: string;
  reportType: string;
}

/** Read model from GET /Templates. `columns` is a CSV of column keys in Excel order. */
export interface ReportTemplate {
  id: number;
  templateName: string;
  reportType: string; // 'COI_GENERATED' | 'PAYMENTS'
  columns: string; // CSV of column keys, in order
  isActive: boolean;
  createdAt?: string | null; // UTC, may lack the trailing 'Z'
  usedBySchedules: number;
}

/** Write model for POST /Templates/Save. `id` 0 = create; `columns` = ordered keys. */
export interface ReportTemplateSaveDto {
  id: number;
  templateName: string;
  reportType: string;
  columns: string[];
  isActive: boolean;
}

/** `data` of GET /ReportSchedule/Options — dropdown sources for the editor dialog. */
export interface ReportScheduleOptions {
  partners: IdName[];
  clients: IdName[];
  reportTypes: string[];
  scopeTypes: string[];
  reportWindows: string[]; // SAME_DAY first, then PREVIOUS_DAY … ALL_TIME
  frequencies: string[];
  paymentStatuses: string[];
  daysOfWeek: ValueLabel[];
  templates: ReportTemplateOption[]; // active templates only
  reportColumns: Record<string, ReportColumnDef[]>; // full ordered catalogue per report type
}

// ===================== defensive mappers =====================
// Dapper-dynamic endpoints serialize PascalCase; typed DTOs serialize camelCase.
// Read both so grids never render blank (see CLAUDE.md).

function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asBool(v: any, dflt = false): boolean {
  if (v == null) return dflt;
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
function pickNum(x: any, ...keys: string[]): number | null {
  const v = pick(x, ...keys);
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function mapSchedule(x: any): ReportScheduleRow {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    scheduleName: pick(x, 'ScheduleName', 'scheduleName') ?? '',
    reportType: pick(x, 'ReportType', 'reportType') ?? '',
    scopeType: pick(x, 'ScopeType', 'scopeType') ?? 'ALL',
    partnerId: pickNum(x, 'PartnerId', 'partnerId'),
    clientId: pickNum(x, 'ClientId', 'clientId'),
    partnerName: pick(x, 'PartnerName', 'partnerName') ?? null,
    clientName: pick(x, 'ClientName', 'clientName') ?? null,
    paymentStatuses: pick(x, 'PaymentStatuses', 'paymentStatuses') ?? null,
    templateId: pickNum(x, 'TemplateId', 'templateId'),
    templateName: pick(x, 'TemplateName', 'templateName') ?? null,
    reportWindow: pick(x, 'ReportWindow', 'reportWindow') ?? '',
    frequency: pick(x, 'Frequency', 'frequency') ?? '',
    runAtTime: pick(x, 'RunAtTime', 'runAtTime') ?? '',
    dayOfWeek: pickNum(x, 'DayOfWeek', 'dayOfWeek'),
    dayOfMonth: pickNum(x, 'DayOfMonth', 'dayOfMonth'),
    isEmail: asBool(pick(x, 'IsEmail', 'isEmail')),
    isWhatsApp: asBool(pick(x, 'IsWhatsApp', 'isWhatsApp')),
    emails: pick(x, 'Emails', 'emails') ?? null,
    whatsAppNumbers: pick(x, 'WhatsAppNumbers', 'whatsAppNumbers') ?? null,
    fileFormat: pick(x, 'FileFormat', 'fileFormat') ?? 'XLSX',
    isActive: asBool(pick(x, 'IsActive', 'isActive')),
    lastRunAt: pick(x, 'LastRunAt', 'lastRunAt') ?? null,
    nextRunAt: pick(x, 'NextRunAt', 'nextRunAt') ?? null,
    createdAt: pick(x, 'CreatedAt', 'createdAt') ?? null,
  };
}

function mapLog(x: any): ReportRunLog {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    scheduleId: asNum(pick(x, 'ScheduleId', 'scheduleId')),
    scheduleName: pick(x, 'ScheduleName', 'scheduleName') ?? null,
    triggeredBy: pick(x, 'TriggeredBy', 'triggeredBy') ?? '',
    runStartedAt: pick(x, 'RunStartedAt', 'runStartedAt') ?? null,
    runCompletedAt: pick(x, 'RunCompletedAt', 'runCompletedAt') ?? null,
    status: pick(x, 'Status', 'status') ?? '',
    rowCount: asNum(pick(x, 'RowCount', 'rowCount')),
    fileUrl: pick(x, 'FileUrl', 'fileUrl') ?? null,
    fileStorageKey: pick(x, 'FileStorageKey', 'fileStorageKey') ?? null,
    emailsSent: asNum(pick(x, 'EmailsSent', 'emailsSent')),
    whatsAppSent: asNum(pick(x, 'WhatsAppSent', 'whatsAppSent')),
    errorMessage: pick(x, 'ErrorMessage', 'errorMessage') ?? null,
  };
}

function mapIdName(x: any): IdName {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    name: pick(x, 'Name', 'name') ?? '',
    code: pick(x, 'Code', 'code') ?? null,
  };
}

function mapValueLabel(x: any): ValueLabel {
  return {
    value: asNum(pick(x, 'Value', 'value')),
    label: pick(x, 'Label', 'label') ?? '',
  };
}

function pickArr(x: any, ...keys: string[]): any[] {
  const v = pick(x, ...keys);
  return Array.isArray(v) ? v : [];
}

function mapColumnDef(x: any): ReportColumnDef {
  return {
    key: pick(x, 'Key', 'key') ?? '',
    label: pick(x, 'Label', 'label') ?? '',
  };
}

function mapTemplateOption(x: any): ReportTemplateOption {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    templateName: pick(x, 'TemplateName', 'templateName') ?? '',
    reportType: pick(x, 'ReportType', 'reportType') ?? '',
  };
}

function mapTemplate(x: any): ReportTemplate {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    templateName: pick(x, 'TemplateName', 'templateName') ?? '',
    reportType: pick(x, 'ReportType', 'reportType') ?? '',
    columns: pick(x, 'Columns', 'columns') ?? '',
    isActive: asBool(pick(x, 'IsActive', 'isActive')),
    createdAt: pick(x, 'CreatedAt', 'createdAt') ?? null,
    usedBySchedules: asNum(pick(x, 'UsedBySchedules', 'usedBySchedules')),
  };
}

/** { COI_GENERATED: [{key,label}…], PAYMENTS: […] } — normalize the type-code keys
 *  to UPPERCASE: .NET's camelCase policy can mangle them (COI_GENERATED → coI_GENERATED,
 *  PAYMENTS → payments) depending on how the backend shapes the object. */
function mapReportColumns(x: any): Record<string, ReportColumnDef[]> {
  const raw = pick<Record<string, any>>(x, 'ReportColumns', 'reportColumns') ?? {};
  const out: Record<string, ReportColumnDef[]> = {};
  for (const key of Object.keys(raw)) {
    out[key.toUpperCase()] = (Array.isArray(raw[key]) ? raw[key] : []).map(mapColumnDef);
  }
  return out;
}

function mapOptions(x: any): ReportScheduleOptions {
  return {
    partners: pickArr(x, 'Partners', 'partners').map(mapIdName),
    clients: pickArr(x, 'Clients', 'clients').map(mapIdName),
    reportTypes: pickArr(x, 'ReportTypes', 'reportTypes').map(String),
    scopeTypes: pickArr(x, 'ScopeTypes', 'scopeTypes').map(String),
    reportWindows: pickArr(x, 'ReportWindows', 'reportWindows').map(String),
    frequencies: pickArr(x, 'Frequencies', 'frequencies').map(String),
    paymentStatuses: pickArr(x, 'PaymentStatuses', 'paymentStatuses').map(String),
    daysOfWeek: pickArr(x, 'DaysOfWeek', 'daysOfWeek').map(mapValueLabel),
    templates: pickArr(x, 'Templates', 'templates').map(mapTemplateOption),
    reportColumns: mapReportColumns(x),
  };
}

function mapEnv<T>(fn: (x: any) => T) {
  return (res: any) => {
    if (res && Array.isArray(res.data)) return { ...res, data: res.data.map(fn) };
    if (res && res.data && typeof res.data === 'object') return { ...res, data: fn(res.data) };
    return res;
  };
}

// ===================== service =====================

/**
 * Recurring COI / payment reports (Excel on S3, delivered by the notification worker).
 * Backend is [Authorize(Roles="Admin")] — non-admins get a real HTTP 403.
 * All `…At` timestamps are UTC; GetAll/Logs may return them WITHOUT the trailing 'Z'
 * while Insert/Update/Enable echo `nextRunAt` WITH it — treat both as UTC.
 */
@Injectable({ providedIn: 'root' })
export class ReportScheduleService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  getAll() {
    return this.http
      .get<ApiEnvelope<ReportScheduleRow[]>>(`${this.api}/ReportSchedule/GetAll`)
      .pipe(map(mapEnv(mapSchedule)));
  }

  getById(id: number) {
    return this.http
      .get<ApiEnvelope<ReportScheduleRow>>(`${this.api}/ReportSchedule/GetById`, { params: { id } })
      .pipe(map(mapEnv(mapSchedule)));
  }

  options() {
    return this.http
      .get<ApiEnvelope<ReportScheduleOptions>>(`${this.api}/ReportSchedule/Options`)
      .pipe(map(mapEnv(mapOptions)));
  }

  insert(dto: ReportScheduleDto) {
    return this.http.post<ApiEnvelope>(`${this.api}/ReportSchedule/Insert`, dto);
  }

  update(dto: ReportScheduleDto) {
    return this.http.post<ApiEnvelope>(`${this.api}/ReportSchedule/Update`, dto);
  }

  enable(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ReportSchedule/Enable`, { params: { id } });
  }

  disable(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ReportSchedule/Disable`, { params: { id } });
  }

  delete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ReportSchedule/Delete`, { params: { id } });
  }

  runNow(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ReportSchedule/RunNow`, { params: { id } });
  }

  // -------- column templates --------

  getTemplates() {
    return this.http
      .get<ApiEnvelope<ReportTemplate[]>>(`${this.api}/ReportSchedule/Templates`)
      .pipe(map(mapEnv(mapTemplate)));
  }

  /** 409 on duplicate name / 400 on bad columns arrive as success:false + message in the envelope. */
  saveTemplate(dto: ReportTemplateSaveDto) {
    return this.http.post<ApiEnvelope>(`${this.api}/ReportSchedule/Templates/Save`, dto);
  }

  /** 409 with a message when the template is still used by schedules. */
  deleteTemplate(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/ReportSchedule/Templates/Delete`, { params: { id } });
  }

  logs(scheduleId?: number, top = 50) {
    let params = new HttpParams().set('top', top);
    if (scheduleId) params = params.set('scheduleId', scheduleId);
    return this.http
      .get<ApiEnvelope<ReportRunLog[]>>(`${this.api}/ReportSchedule/Logs`, { params })
      .pipe(map(mapEnv(mapLog)));
  }

  /**
   * Excel bytes for a run, streamed through the API by durable storage KEY (the
   * interceptor attaches the JWT). Never binds the stored presigned FileUrl, which
   * expires with the IAM-role token and, for old rows, points at a deleted bucket.
   */
  downloadFile(logId: number) {
    return this.http.get(`${this.api}/ReportSchedule/DownloadFile`, {
      params: new HttpParams().set('logId', logId),
      responseType: 'blob',
    });
  }
}
